import React, { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import {
    MdAdd,
    MdCheckCircle,
    MdClose,
    MdDelete,
    MdEdit,
    MdEmojiEvents,
    MdRefresh,
    MdSave,
    MdStar,
} from 'react-icons/md';
import { NewTournament, Settings, Team, TournamentWithRelations, UpdateTournament } from '../@backend';
import useApi from '../hooks/useApi';
import { userState } from '../store';
import { Menu } from '../ui/layout/Menu';
import { FooterCmp } from '../ui/layout/Footer';
import { PageHeader } from '../ui/ds/PageHeader';
import { Card } from '../ui/ds/Card';
import { Btn } from '../ui/ds/Btn';
import { Badge } from '../ui/ds/Badge';
import { Input } from '../ui/ds/Input';
import { AdminSubmenu } from './AdminSubmenu';

type TournamentForm = {
    name: string;
    sport: string;
    dateStart: string;
    dateEnd: string;
    show: boolean;
};

const emptyForm: TournamentForm = {
    name: '',
    sport: 'hockey',
    dateStart: '',
    dateEnd: '',
    show: true,
};

const toDateInput = (date?: string) => date ? date.slice(0, 10) : '';

const toForm = (tournament: TournamentWithRelations): TournamentForm => ({
    name: tournament.name ?? '',
    sport: tournament.sport ?? '',
    dateStart: toDateInput(tournament.dateStart),
    dateEnd: toDateInput(tournament.dateEnd),
    show: tournament.show ?? false,
});

const toPayload = (form: TournamentForm): NewTournament => ({
    name: form.name.trim(),
    sport: form.sport.trim(),
    dateStart: form.dateStart || undefined,
    dateEnd: form.dateEnd || undefined,
    show: form.show,
});

const getActiveTournamentId = (settings?: Settings[] | null): number | null => {
    const settingValue = settings?.find(setting => setting.key === 'activeTournamentId')?.value;
    if (!settingValue) return null;
    const direct = Number(settingValue);
    if (Number.isFinite(direct)) return direct;

    try {
        const parsed = JSON.parse(settingValue);
        const id = Number(parsed?.tournamentId ?? parsed?.activeTournamentId ?? parsed?.id);
        return Number.isFinite(id) ? id : null;
    } catch {
        return null;
    }
};

function TournamentFields({
    form,
    onChange,
}: {
    form: TournamentForm;
    onChange: (form: TournamentForm) => void;
}) {
    return (
        <div className="flex flex-col gap-1">
            <Input
                label="Název"
                value={form.name}
                onChange={e => onChange({ ...form, name: e.target.value })}
                placeholder="Název turnaje"
                required
            />
            <Input
                label="Sport"
                value={form.sport}
                onChange={e => onChange({ ...form, sport: e.target.value })}
                placeholder="hockey"
                required
            />
            <div className="grid sm:grid-cols-2 gap-3">
                <Input
                    label="Datum od"
                    type="date"
                    value={form.dateStart}
                    onChange={e => onChange({ ...form, dateStart: e.target.value })}
                />
                <Input
                    label="Datum do"
                    type="date"
                    value={form.dateEnd}
                    onChange={e => onChange({ ...form, dateEnd: e.target.value })}
                />
            </div>
            <label className="flex items-center gap-3 cursor-pointer mb-4">
                <input
                    type="checkbox"
                    checked={form.show}
                    onChange={e => onChange({ ...form, show: e.target.checked })}
                    className="w-5 h-5 accent-brand-blue"
                />
                <span className="tb-body text-text-primary">Zobrazit v aplikaci</span>
            </label>
        </div>
    );
}

export const AdminTournaments = () => {
    const userSnap = useSnapshot(userState);
    const { backend } = useApi();

    const [tournaments, setTournaments] = useState<TournamentWithRelations[]>([]);
    const [activeTournamentId, setActiveTournamentId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [createForm, setCreateForm] = useState<TournamentForm>(emptyForm);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editForm, setEditForm] = useState<TournamentForm>(emptyForm);
    const [workingId, setWorkingId] = useState<number | 'new' | 'active' | 'bonus' | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [bonusTeams, setBonusTeams] = useState<Team[]>([]);
    const [bonusTeamId, setBonusTeamId] = useState('');

    const visibleTournaments = useMemo(
        () => tournaments.filter(t => t.show).length,
        [tournaments],
    );

    const clearFeedback = () => {
        setMessage(null);
        setError(null);
    };

    const showError = (e: any, fallback: string) => {
        setError(e?.body?.message ?? e?.message ?? fallback);
        setMessage(null);
    };

    const loadTournaments = async (resetFeedback = true) => {
        setLoading(true);
        if (resetFeedback) clearFeedback();
        try {
            const [loadedTournaments, settings] = await Promise.all([
                backend.tournaments.tournamentControllerGetAllTournaments(true),
                backend.settingsController.settingsControllerGetSystemSettings().catch(() => null),
            ]);
            setTournaments(loadedTournaments ?? []);
            setActiveTournamentId(getActiveTournamentId(settings));
        } catch (e: any) {
            showError(e, 'Turnaje se nepodařilo načíst.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userSnap.isLoggedIn && userSnap.userProfile.role === 'Admin') {
            loadTournaments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSnap.isLoggedIn, userSnap.userProfile.role]);

    useEffect(() => {
        if (!activeTournamentId) {
            setBonusTeams([]);
            return;
        }
        backend.teams.teamsControllerGetAllTeams(activeTournamentId)
            .then(loaded => setBonusTeams(loaded ?? []))
            .catch(e => showError(e, 'Týmy se nepodařilo načíst.'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTournamentId]);

    const applyBonusQuestion = async () => {
        const teamId = Number(bonusTeamId);
        if (!Number.isFinite(teamId) || teamId <= 0) return;

        const team = bonusTeams.find(t => t.id === teamId);
        const teamName = team?.nameCountry ?? team?.name ?? `ID ${teamId}`;
        if (!window.confirm(
            `Opravdu vyhodnotit bonusovou otázku s vítězem ${teamName}? `
            + 'Všichni uživatelé se správnou odpovědí získají +50 bodů. Tuto akci nelze vrátit.'
        )) return;

        setWorkingId('bonus');
        clearFeedback();
        try {
            const result = await backend.admin.adminControllerAdminApplyBonusQuestion({ teamId });
            setMessage(
                `Bonusová otázka byla vyhodnocena. +${result.pointsAwarded ?? 50} bodů získalo `
                + `${result.usersAwarded ?? 0} uživatelů (vítěz: ${teamName}).`
            );
            setBonusTeamId('');
        } catch (e: any) {
            showError(e, 'Bonusovou otázku se nepodařilo vyhodnotit.');
        } finally {
            setWorkingId(null);
        }
    };

    const createTournament = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!createForm.name.trim() || !createForm.sport.trim()) return;

        setWorkingId('new');
        clearFeedback();
        try {
            await backend.tournaments.tournamentControllerAddTournament(toPayload(createForm));
            setCreateForm(emptyForm);
            setMessage('Turnaj byl vytvořen.');
            await loadTournaments(false);
        } catch (e: any) {
            showError(e, 'Turnaj se nepodařilo vytvořit.');
        } finally {
            setWorkingId(null);
        }
    };

    const startEdit = (tournament: TournamentWithRelations) => {
        if (!tournament.id) return;
        setEditingId(tournament.id);
        setEditForm(toForm(tournament));
        clearFeedback();
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm(emptyForm);
    };

    const saveTournament = async (id?: number) => {
        if (!id || !editForm.name.trim() || !editForm.sport.trim()) return;

        setWorkingId(id);
        clearFeedback();
        try {
            await backend.tournaments.tournamentControllerUpdateTournament(id, toPayload(editForm) as UpdateTournament);
            setMessage('Turnaj byl uložen.');
            cancelEdit();
            await loadTournaments(false);
        } catch (e: any) {
            showError(e, 'Turnaj se nepodařilo uložit.');
        } finally {
            setWorkingId(null);
        }
    };

    const deleteTournament = async (tournament: TournamentWithRelations) => {
        if (!tournament.id) return;
        if (!window.confirm(`Opravdu smazat turnaj ${tournament.name}? Tuto akci nelze vrátit.`)) return;

        setWorkingId(tournament.id);
        clearFeedback();
        try {
            await backend.tournaments.tournamentControllerDeleteTournament(tournament.id);
            setMessage('Turnaj byl smazán.');
            if (activeTournamentId === tournament.id) setActiveTournamentId(null);
            await loadTournaments(false);
        } catch (e: any) {
            showError(e, 'Turnaj se nepodařilo smazat.');
        } finally {
            setWorkingId(null);
        }
    };

    const activateTournament = async (id?: number) => {
        if (!id) return;

        setWorkingId('active');
        clearFeedback();
        try {
            await backend.settingsController.settingsControllerUpdateActiveTournament({ tournamentId: id });
            setActiveTournamentId(id);
            setMessage('Aktivní turnaj byl nastaven.');
        } catch (e: any) {
            showError(e, 'Aktivní turnaj se nepodařilo nastavit.');
        } finally {
            setWorkingId(null);
        }
    };

    if (!userSnap.isLoggedIn) {
        return (
            <>
                <Menu />
                <PageHeader title="Administrace" subtitle="Správa uživatelů a systému" />
                <div className="max-w-xl mx-auto px-4 py-16 text-center">
                    <span className="text-[56px]">🔒</span>
                    <p className="tb-h2 text-text-primary mt-4 mb-2">Pouze pro administrátory</p>
                    <p className="tb-body text-text-muted">Pro pokračování se přihlaste administrátorským účtem.</p>
                </div>
                <FooterCmp />
            </>
        );
    }

    if (userSnap.userProfile.role !== 'Admin') {
        return (
            <>
                <Menu />
                <PageHeader title="Administrace" subtitle="Správa uživatelů a systému" />
                <div className="max-w-xl mx-auto px-4 py-16 text-center">
                    <span className="text-[56px]">🚫</span>
                    <p className="tb-h2 text-text-primary mt-4 mb-2">Nemáte oprávnění</p>
                    <p className="tb-body text-text-muted">Tato stránka je dostupná pouze administrátorům.</p>
                </div>
                <FooterCmp />
            </>
        );
    }

    return (
        <>
            <Menu />
            <PageHeader
                title="Administrace"
                subtitle={`${tournaments.length} turnajů · ${visibleTournaments} zobrazeno`}
                right={
                    <button
                        type="button"
                        onClick={() => loadTournaments()}
                        disabled={loading}
                        title="Obnovit turnaje"
                        className="w-10 h-10 flex items-center justify-center rounded-content bg-white/15 hover:bg-white/25 transition-colors text-white disabled:opacity-50"
                    >
                        <MdRefresh className={`text-[22px] ${loading ? 'animate-spin' : ''}`} />
                    </button>
                }
            />
            <AdminSubmenu />

            <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex flex-col gap-6">
                {(message || error) && (
                    <div className={[
                        'flex items-center gap-3 rounded-content px-4 py-3',
                        error
                            ? 'bg-semantic-danger/10 border border-semantic-danger/20'
                            : 'bg-semantic-success/10 border border-semantic-success/20',
                    ].join(' ')}>
                        <Badge variant={error ? 'danger' : 'success'}>
                            {error ? 'Chyba' : 'OK'}
                        </Badge>
                        <span className="tb-body text-text-primary">{error ?? message}</span>
                        <button
                            onClick={() => { setError(null); setMessage(null); }}
                            className="ml-auto text-text-muted hover:text-text-primary transition-colors"
                        >
                            <MdClose className="text-[18px]" />
                        </button>
                    </div>
                )}

                <div className="grid lg:grid-cols-[360px_minmax(0,1fr)] gap-6 items-start">
                    <div className="flex flex-col gap-6">
                        <Card>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-11 h-11 rounded-content bg-brand-blue/10 text-brand-blue flex items-center justify-center">
                                    <MdAdd className="text-[24px]" />
                                </div>
                                <div>
                                    <p className="tb-h3 text-text-primary">Nový turnaj</p>
                                    <p className="tb-caption">Vytvoření lokálního turnaje.</p>
                                </div>
                            </div>

                            <form onSubmit={createTournament}>
                                <TournamentFields form={createForm} onChange={setCreateForm} />
                                <Btn
                                    type="submit"
                                    variant="primary"
                                    size="md"
                                    fullWidth
                                    icon={<MdAdd />}
                                    disabled={workingId !== null || !createForm.name.trim() || !createForm.sport.trim()}
                                >
                                    {workingId === 'new' ? 'Vytvářím…' : 'Vytvořit turnaj'}
                                </Btn>
                            </form>
                        </Card>

                        <Card>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-11 h-11 rounded-content bg-brand-orange/10 text-brand-orange flex items-center justify-center">
                                    <MdEmojiEvents className="text-[24px]" />
                                </div>
                                <div>
                                    <p className="tb-h3 text-text-primary">Bonusová otázka</p>
                                    <p className="tb-caption">Vítěz šampionátu, +50 bodů za správný tip.</p>
                                </div>
                            </div>

                            <div className="mb-4">
                                <p className="tb-label mb-1.5">Vítězný tým</p>
                                <select
                                    value={bonusTeamId}
                                    onChange={e => setBonusTeamId(e.target.value)}
                                    disabled={!activeTournamentId || bonusTeams.length === 0}
                                    className="w-full h-[50px] bg-bg-input border-2 border-border-neutral rounded-input px-4 text-[15px] font-semibold text-text-primary font-nunito focus:border-brand-orange focus:outline-none transition-colors disabled:bg-bg-inputSoft disabled:text-text-muted"
                                >
                                    <option value="">
                                        {!activeTournamentId
                                            ? 'Nejdřív nastavte aktivní turnaj'
                                            : bonusTeams.length === 0
                                                ? 'Aktivní turnaj nemá žádné týmy'
                                                : 'Vyberte tým…'}
                                    </option>
                                    {[...bonusTeams]
                                        .sort((a, b) => (a.nameCountry ?? a.name).localeCompare(b.nameCountry ?? b.name, 'cs'))
                                        .map(team => (
                                            <option key={team.id} value={team.id}>
                                                {team.nameCountry ?? team.name}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            <Btn
                                type="button"
                                variant="orange"
                                size="md"
                                fullWidth
                                icon={<MdEmojiEvents />}
                                onClick={applyBonusQuestion}
                                disabled={!bonusTeamId || workingId !== null}
                            >
                                {workingId === 'bonus' ? 'Vyhodnocuji…' : 'Připsat bonusové body'}
                            </Btn>
                        </Card>
                    </div>

                    <Card padding="none">
                        <div className="hidden md:grid md:grid-cols-[1.5fr_0.8fr_1fr_0.8fr_0.8fr_1.8fr] gap-0 px-5 py-3 bg-bg-app border-b border-border-neutral rounded-t-content">
                            {['Turnaj', 'Sport', 'Datum', 'Týmy', 'Stav', 'Akce'].map(col => (
                                <span key={col} className="tb-label">{col}</span>
                            ))}
                        </div>

                        {loading && (
                            <div className="flex items-center justify-center py-16">
                                <MdRefresh className="text-[32px] text-brand-blue animate-spin" />
                            </div>
                        )}

                        {!loading && tournaments.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-16 gap-3">
                                <MdEmojiEvents className="text-[52px] text-text-muted/40" />
                                <p className="tb-h3 text-text-primary">Žádné turnaje</p>
                                <p className="tb-caption">Zatím není vytvořený žádný turnaj.</p>
                            </div>
                        )}

                        {!loading && tournaments.map(tournament => {
                            const isEditing = editingId === tournament.id;
                            const isActive = activeTournamentId === tournament.id;
                            const teamCount = tournament.teams?.length ?? 0;

                            return (
                                <div
                                    key={tournament.id}
                                    className="flex flex-col md:grid md:grid-cols-[1.5fr_0.8fr_1fr_0.8fr_0.8fr_1.8fr] items-start md:items-center gap-3 md:gap-0 px-5 py-4 border-b border-divider-row last:border-b-0 hover:bg-bg-app transition-colors"
                                >
                                    {isEditing ? (
                                        <div className="md:col-span-5 w-full">
                                            <TournamentFields form={editForm} onChange={setEditForm} />
                                        </div>
                                    ) : (
                                        <>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="tb-h4 text-text-primary leading-tight">{tournament.name}</p>
                                                    {isActive && (
                                                        <Badge variant="info">
                                                            <MdStar className="inline mr-1" />Aktivní
                                                        </Badge>
                                                    )}
                                                </div>
                                                <p className="tb-caption">ID {tournament.id}</p>
                                            </div>

                                            <p className="tb-body text-text-muted">{tournament.sport}</p>

                                            <p className="tb-body text-text-muted">
                                                {[toDateInput(tournament.dateStart), toDateInput(tournament.dateEnd)]
                                                    .filter(Boolean)
                                                    .join(' - ') || '—'}
                                            </p>

                                            <p className="text-[16px] font-black font-nunito text-text-primary">{teamCount}</p>

                                            <div>
                                                {tournament.show ? (
                                                    <Badge variant="success">
                                                        <MdCheckCircle className="inline mr-1" />Zobrazeno
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="neutral">Skryto</Badge>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    <div className="flex flex-wrap gap-2">
                                        {isEditing ? (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => saveTournament(tournament.id)}
                                                    disabled={workingId === tournament.id}
                                                    title="Uložit turnaj"
                                                    className="flex items-center gap-1 h-8 px-3 bg-semantic-success text-white rounded-btn text-[12px] font-bold font-nunito hover:opacity-90 transition-opacity disabled:opacity-50"
                                                >
                                                    <MdSave className="text-[15px]" />
                                                    {workingId === tournament.id ? '…' : 'Uložit'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={cancelEdit}
                                                    title="Zrušit úpravy"
                                                    className="flex items-center gap-1 h-8 px-3 bg-text-primary text-white rounded-btn text-[12px] font-bold font-nunito hover:opacity-90 transition-opacity"
                                                >
                                                    <MdClose className="text-[15px]" />
                                                    Zrušit
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                {!isActive && (
                                                    <button
                                                        type="button"
                                                        onClick={() => activateTournament(tournament.id)}
                                                        disabled={workingId !== null}
                                                        title="Nastavit jako aktivní turnaj"
                                                        className="flex items-center gap-1 h-8 px-3 bg-brand-orange text-white rounded-btn text-[12px] font-bold font-nunito hover:opacity-90 transition-opacity disabled:opacity-50"
                                                    >
                                                        <MdStar className="text-[15px]" />
                                                        Aktivní
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => startEdit(tournament)}
                                                    disabled={workingId !== null}
                                                    title="Upravit turnaj"
                                                    className="flex items-center gap-1 h-8 px-3 bg-brand-blue text-white rounded-btn text-[12px] font-bold font-nunito hover:opacity-90 transition-opacity disabled:opacity-50"
                                                >
                                                    <MdEdit className="text-[15px]" />
                                                    Upravit
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => deleteTournament(tournament)}
                                                    disabled={workingId !== null}
                                                    title="Smazat turnaj"
                                                    className="flex items-center gap-1 h-8 px-3 bg-semantic-danger text-white rounded-btn text-[12px] font-bold font-nunito hover:opacity-90 transition-opacity disabled:opacity-50"
                                                >
                                                    <MdDelete className="text-[15px]" />
                                                    Smazat
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </Card>
                </div>
            </main>

            <FooterCmp />
        </>
    );
};
